import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import {
  ArrowLeft, ExternalLink, Copy, RefreshCw, Trash2, Loader2, Eye, Inbox as InboxIcon,
  Mail, Phone, User as UserIcon, Check, X, DoorOpen, Upload, ImagePlus,
  Trophy, Zap, Gavel, Handshake,
} from "lucide-react";

const OFFER_STATUS_BADGE = {
  new: { text: "New", bg: "#DCFCE7", fg: "#166534" },
  reviewing: { text: "Reviewing", bg: "#FEF3C7", fg: "#78350F" },
  accepted: { text: "Accepted", bg: "#DBEAFE", fg: "#1E40AF" },
  rejected: { text: "Rejected", bg: "#FEE2E2", fg: "#991B1B" },
  expired: { text: "Expired", bg: "#F1F5F9", fg: "#64748B" },
};

export default function DispoRoomDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [offers, setOffers] = useState([]);
  const [closing, setClosing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingComps, setRefreshingComps] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, oRes, cRes] = await Promise.all([
        api.get(`/dispo/rooms/${id}`),
        api.get(`/dispo/rooms/${id}/offers`),
        api.get(`/dispo/closings`, { params: { room_id: id } }).catch(() => ({ data: { closings: [] } })),
      ]);
      setRoom(rRes.data.room);
      setOffers(oRes.data.offers || []);
      setClosing((cRes.data.closings || [])[0] || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load Deal Room");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const copyPublicLink = () => {
    if (!room) return;
    const url = `${window.location.origin}/deal/${room.slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Public link copied 📋"));
  };

  const refreshComps = async () => {
    setRefreshingComps(true);
    try {
      const { data } = await api.post(`/dispo/rooms/${id}/refresh-comps`);
      setRoom((r) => ({ ...r, comps: data.comps }));
      toast.success(data.comps ? "Comps refreshed" : "No comps found for this address");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to refresh comps");
    } finally {
      setRefreshingComps(false);
    }
  };

  const deleteRoom = async () => {
    if (!confirm("Delete this Deal Room and all its offers? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/dispo/rooms/${id}`);
      toast.success("Deal Room deleted");
      nav("/app/dispo");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
      setDeleting(false);
    }
  };

  const setOfferStatus = async (offerId, status) => {
    try {
      const { data } = await api.patch(`/dispo/rooms/${id}/offers/${offerId}`, { status });
      setOffers((prev) => prev.map((o) => (o.id === offerId ? data.offer : o)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update offer");
    }
  };

  const sendToClosing = async (offerId) => {
    try {
      const { data } = await api.post("/dispo/closings", { room_id: id, offer_id: offerId });
      setClosing(data.closing);
      toast.success("Sent to Closing Studio 🤝");
      nav("/app/closings");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to open closing");
    }
  };

  if (loading) return <div className="p-10 text-center text-[var(--ink-3)]" data-testid="dispo-room-loading">Loading…</div>;
  if (!room) return <div className="p-10 text-center" data-testid="dispo-room-not-found">Deal Room not found. <Link to="/app/dispo" className="underline">Back to list</Link></div>;

  return (
    <div className="space-y-6" data-testid="dispo-room-detail-page">
      <Link to="/app/dispo" className="inline-flex items-center gap-1 text-sm text-[var(--ink-2)] hover:text-[var(--ink)]" data-testid="dispo-back-link">
        <ArrowLeft size={14} />Back to Deal Rooms
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] mb-1">{room.status}</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight" data-testid="dispo-room-title">{room.title}</h1>
          <div className="text-sm text-[var(--ink-2)] mt-1">{room.property_address}</div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={copyPublicLink} data-testid="dispo-copy-link">
            <Copy size={14} />Copy public link
          </button>
          <a
            href={`/deal/${room.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-sm flex items-center gap-1"
            data-testid="dispo-open-public"
          >
            Open <ExternalLink size={12} />
          </a>
          <button type="button" className="btn-ghost text-sm" onClick={deleteRoom} disabled={deleting} data-testid="dispo-delete-room">
            <Trash2 size={14} />{deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Asking" value={fmtMoney(room.asking_price)} />
        <Stat label="ARV" value={fmtMoney(room.arv)} />
        <Stat label="Repairs" value={fmtMoney(room.repair_estimate)} />
        <Stat label="Views" value={room.views ?? 0} icon={Eye} />
        <Stat label="Offers" value={offers.length} icon={InboxIcon} testid="dispo-stat-offers" />
      </div>

      <PhotoManager
        room={room}
        onChange={(photos) => setRoom((r) => ({ ...r, photo_urls: photos }))}
      />

      <AuctionLeaderboard roomId={id} />

      <AutoCounterSettings
        room={room}
        onChange={(patch) => setRoom((r) => ({ ...r, ...patch }))}
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)]">RentCast comps</div>
          <button type="button" className="btn-ghost text-xs" onClick={refreshComps} disabled={refreshingComps} data-testid="dispo-refresh-comps">
            {refreshingComps ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {refreshingComps ? "Fetching…" : "Refresh"}
          </button>
        </div>
        <div className="rounded-lg border border-[var(--line)] bg-white p-4 text-sm">
          {!room.comps ? (
            <div className="text-[var(--ink-3)]">No comps cached yet. Click Refresh to pull from RentCast.</div>
          ) : (
            <div className="space-y-1">
              {room.comps.avm && (
                <div><strong>AVM estimate:</strong> {fmtMoney(room.comps.avm?.price ?? room.comps.avm?.value ?? room.comps.avm?.priceEstimate)}</div>
              )}
              {room.comps.property && (
                <div className="text-[var(--ink-2)]">
                  Property record found · fetched {room.comps.fetched_at?.split("T")[0]}
                </div>
              )}
              {!room.comps.avm && !room.comps.property && (
                <div className="text-[var(--ink-3)]">Comps unavailable for this address.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)]">Offer Inbox</div>
          {closing && <ClosingTimelinePill closing={closing} />}
        </div>
        {offers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-3)]" data-testid="dispo-offers-empty">
            <InboxIcon size={28} className="mx-auto mb-2" />
            No offers yet. Share your public link to start collecting.
          </div>
        ) : (
          <div className="space-y-3" data-testid="dispo-offers-list">
            {offers.map((o) => {
              const badge = OFFER_STATUS_BADGE[o.status] || OFFER_STATUS_BADGE.new;
              return (
                <div
                  key={o.id}
                  className="rounded-lg border border-[var(--line)] bg-white p-4"
                  data-testid={`dispo-offer-${o.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="font-semibold flex items-center gap-1"><UserIcon size={14} />{o.buyer_name}</div>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
                          style={{ background: badge.bg, color: badge.fg }}
                        >{badge.text}</span>
                      </div>
                      <div className="text-xs text-[var(--ink-2)] flex flex-wrap gap-3">
                        <a href={`mailto:${o.buyer_email}`} className="flex items-center gap-1 hover:underline"><Mail size={11} />{o.buyer_email}</a>
                        {o.buyer_phone && <a href={`tel:${o.buyer_phone}`} className="flex items-center gap-1 hover:underline"><Phone size={11} />{o.buyer_phone}</a>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-xl">{fmtMoney(o.offer_price)}</div>
                      <div className="text-xs text-[var(--ink-3)]">
                        {o.emd_amount ? `EMD ${fmtMoney(o.emd_amount)}` : "No EMD"} · {o.close_date || "Close TBD"}
                      </div>
                    </div>
                  </div>
                  {o.notes && (
                    <div className="mt-3 text-sm text-[var(--ink-2)] border-t border-[var(--line)] pt-3 whitespace-pre-wrap">{o.notes}</div>
                  )}
                  {o.status === "new" && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--line)]">
                      <button type="button" className="btn-ghost text-xs" onClick={() => setOfferStatus(o.id, "reviewing")} data-testid={`dispo-offer-review-${o.id}`}>
                        Mark reviewing
                      </button>
                      <button type="button" className="btn-money text-xs" onClick={() => setOfferStatus(o.id, "accepted")} data-testid={`dispo-offer-accept-${o.id}`}>
                        <Check size={12} />Accept
                      </button>
                      <button type="button" className="btn-ghost text-xs" onClick={() => setOfferStatus(o.id, "rejected")} data-testid={`dispo-offer-reject-${o.id}`}>
                        <X size={12} />Reject
                      </button>
                    </div>
                  )}
                  {o.status === "reviewing" && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--line)]">
                      <button type="button" className="btn-money text-xs" onClick={() => setOfferStatus(o.id, "accepted")} data-testid={`dispo-offer-accept-${o.id}`}>
                        <Check size={12} />Accept
                      </button>
                      <button type="button" className="btn-ghost text-xs" onClick={() => setOfferStatus(o.id, "rejected")} data-testid={`dispo-offer-reject-${o.id}`}>
                        <X size={12} />Reject
                      </button>
                    </div>
                  )}
                  {o.status === "accepted" && !closing && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--line)]">
                      <button
                        type="button"
                        className="btn-money text-xs"
                        onClick={() => sendToClosing(o.id)}
                        data-testid={`dispo-offer-send-closing-${o.id}`}
                      >
                        <Handshake size={12} />Send to closing
                      </button>
                      <span className="text-[11px] text-[var(--ink-3)]">Hands off buyer + price to Closing Studio →</span>
                    </div>
                  )}
                  {o.status === "accepted" && closing && closing.offer_id === o.id && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--line)]">
                      <Link
                        to="/app/closings"
                        className="btn-ghost text-xs"
                        data-testid={`dispo-offer-open-closing-${o.id}`}
                      >
                        <Handshake size={12} />Open in Closing Studio →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-0.5 flex items-center gap-1">
        {Icon && <Icon size={10} />}{label}
      </div>
      <div className="font-display font-bold text-lg">{value}</div>
    </div>
  );
}

const CLOSING_STAGE_LABEL = {
  opened: "Opened", title_ordered: "Title Ordered", inspection: "Inspection",
  appraisal: "Appraisal", clear_to_close: "Clear to Close", funded: "Funded",
  closed_won: "Closed 🎉", dead: "Dead",
};
const CLOSING_STAGE_COLOR = {
  opened: "#94A3B8", title_ordered: "#0EA5E9", inspection: "#8B5CF6",
  appraisal: "#F59E0B", clear_to_close: "#10B981", funded: "#059669",
  closed_won: "#0B1D3B", dead: "#991B1B",
};

/**
 * Tiny inline pill above the Offer Inbox showing the linked closing status.
 * Renders only when this Deal Room has a closing in progress. Clicks through
 * to the Closing Studio so operators can drill in without leaving the room.
 */
function ClosingTimelinePill({ closing }) {
  const stage = closing.stage || "opened";
  const color = CLOSING_STAGE_COLOR[stage] || "#94A3B8";
  const label = CLOSING_STAGE_LABEL[stage] || stage;

  let daysCopy = null;
  if (closing.target_close_date) {
    const target = new Date(closing.target_close_date);
    if (!isNaN(target.getTime())) {
      const diff = Math.round((target - new Date()) / (1000 * 60 * 60 * 24));
      if (diff > 0) daysCopy = `${diff}d to close`;
      else if (diff === 0) daysCopy = "closes today";
      else daysCopy = `${Math.abs(diff)}d past target`;
    }
  }

  return (
    <Link
      to="/app/closings"
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition hover:shadow-sm"
      style={{ background: color + "18", borderColor: color + "55", color }}
      data-testid="dispo-room-closing-pill"
    >
      <Handshake size={11} />
      <span className="text-[var(--ink-2)]">In closing ·</span>
      <span>{label}</span>
      {daysCopy && (
        <>
          <span className="text-[var(--ink-3)]">·</span>
          <span className="text-[var(--ink-2)]">{daysCopy}</span>
        </>
      )}
    </Link>
  );
}


/**
 * PhotoManager — drag-drop or click-to-select image uploader for a Deal Room.
 * Uploads happen one at a time so we get per-file error messaging and progress.
 * Deleting soft-removes the underlying object and pops it from the room's
 * photo_urls array.
 */
function PhotoManager({ room, onChange }) {
  const fileInput = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [deleting, setDeleting] = useState({});
  const photos = room.photo_urls || [];
  const cap = 24;
  const remaining = cap - photos.length;

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f && f.type.startsWith("image/"));
    if (!list.length) {
      toast.error("Only image files (JPEG, PNG, WebP, GIF) are supported");
      return;
    }
    if (list.length > remaining) {
      toast.error(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} can be added — max ${cap} per room`);
      return;
    }
    setUploading(list.length);
    const newUrls = [...photos];
    for (const f of list) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post(`/dispo/rooms/${room.id}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
        onChange([...newUrls]);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || `Failed to upload ${f.name}`);
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
    toast.success(`${list.length} photo${list.length > 1 ? "s" : ""} uploaded 📸`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const deletePhoto = async (url) => {
    if (!confirm("Remove this photo from the Deal Room?")) return;
    setDeleting((d) => ({ ...d, [url]: true }));
    try {
      await api.delete(`/dispo/rooms/${room.id}/photos`, { params: { url } });
      onChange(photos.filter((p) => p !== url));
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting((d) => { const n = { ...d }; delete n[url]; return n; });
    }
  };

  return (
    <div data-testid="dispo-photo-manager">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-[var(--ink-3)]">Photos ({photos.length}/{cap})</div>
        {uploading > 0 && (
          <div className="text-xs text-[var(--ink-2)] flex items-center gap-1">
            <Loader2 size={12} className="animate-spin" /> Uploading {uploading}…
          </div>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        className={`rounded-lg border-2 border-dashed p-6 mb-3 text-center cursor-pointer transition ${dragging ? "bg-amber-50 border-amber-400" : "bg-white border-[var(--line)] hover:border-[var(--ink-3)]"}`}
        data-testid="dispo-photo-dropzone"
      >
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="dispo-photo-file-input"
        />
        <Upload size={22} className="mx-auto text-[var(--ink-3)] mb-1.5" />
        <div className="text-sm font-semibold text-[var(--ink)]">
          {dragging ? "Drop to upload" : "Drop images here, or click to select"}
        </div>
        <div className="text-[11px] text-[var(--ink-3)] mt-0.5">
          JPEG / PNG / WebP / GIF · Max 8MB each · Up to {cap} per room
        </div>
      </div>

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dispo-photo-grid">
          {photos.map((u) => (
            <div key={u} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-[var(--line)] bg-[var(--line)] group">
              <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => deletePhoto(u)}
                disabled={!!deleting[u]}
                className="absolute top-1.5 right-1.5 bg-white/95 hover:bg-red-50 text-red-600 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition shadow-sm"
                title="Remove photo"
                data-testid={`dispo-photo-delete-${photos.indexOf(u)}`}
              >
                {deleting[u] ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * AuctionLeaderboard — live "highest bid" board. Polls every 15s while the
 * page is open so a wholesaler watching their inbox during an active push
 * sees new offers slide in without a manual refresh.
 */
function AuctionLeaderboard({ roomId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/dispo/rooms/${roomId}/leaderboard`);
        if (!cancelled) setData(data);
      } catch { /* transient */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [roomId]);

  if (loading) return null;
  if (!data || (data.stats?.count || 0) === 0) return null;

  const { top_offers, stats, asking_price } = data;
  const leader = top_offers[0];
  const vsAsk = asking_price && leader ? Math.round((leader.offer_price / asking_price - 1) * 100) : null;
  return (
    <div className="rounded-xl border-2 p-5 shadow-sm" style={{ borderColor: "#F59E0B", background: "linear-gradient(135deg,#FFFDF7 0%,#FFF7E4 100%)" }} data-testid="dispo-leaderboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-[#F59E0B]" />
          <span className="font-display font-extrabold text-xl">Auction Leaderboard</span>
          <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">LIVE</span>
        </div>
        <div className="flex gap-4 text-xs text-[var(--ink-2)]">
          <span><b className="text-[var(--ink)]">{stats.live_count}</b> live · {stats.count} total</span>
          {stats.avg_price && <span>Avg <b className="text-[var(--ink)]">{fmtMoney(stats.avg_price)}</b></span>}
        </div>
      </div>

      <div className="space-y-2">
        {top_offers.map((o, i) => {
          const isLeader = i === 0;
          return (
            <div
              key={o.id}
              className={`rounded-lg p-3 flex items-center justify-between gap-3 ${isLeader ? "bg-white border-2 border-[#F59E0B] shadow-sm" : "bg-white/60 border border-[var(--line)]"}`}
              data-testid={`dispo-leaderboard-rank-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm ${isLeader ? "bg-[#F59E0B] text-white" : "bg-[var(--line)] text-[var(--ink-2)]"}`}>
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{o.buyer_name}</div>
                  <div className="text-xs text-[var(--ink-2)] truncate">{o.buyer_email}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-black text-lg">{fmtMoney(o.offer_price)}</div>
                {isLeader && vsAsk != null && (
                  <div className={`text-[10px] font-semibold ${vsAsk >= 0 ? "text-emerald-700" : "text-amber-700"}`}>
                    {vsAsk >= 0 ? "+" : ""}{vsAsk}% vs asking
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * AutoCounterSettings — toggle + bump amount, saves via PATCH /rooms/{id}.
 * Explains the mechanic in-line so operators don't need a doc to understand it.
 */
function AutoCounterSettings({ room, onChange }) {
  const [enabled, setEnabled] = useState(!!room.auto_counter_enabled);
  const [bump, setBump] = useState(room.auto_counter_bump ?? 2500);
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/dispo/rooms/${room.id}`, next);
      onChange({
        auto_counter_enabled: data.room.auto_counter_enabled,
        auto_counter_bump: data.room.auto_counter_bump,
      });
      toast.success("Auto-counter updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    save({ auto_counter_enabled: next });
  };

  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-5" data-testid="dispo-autocounter">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-lg p-2 shrink-0" style={{ background: enabled ? "#FEF3C7" : "#F1F5F9" }}>
            <Zap size={18} className={enabled ? "text-amber-700" : "text-[var(--ink-3)]"} />
          </div>
          <div>
            <div className="font-display font-bold text-base">Auto-Counter</div>
            <p className="text-xs text-[var(--ink-2)] leading-relaxed mt-0.5 max-w-md">
              When a new offer isn&apos;t the highest, iSpeedToClose auto-emails the buyer:
              &ldquo;you&apos;re being outbid — raise to <b>${(Number(bump) || 0).toLocaleString()}</b> above the current leader to win.&rdquo; Max one counter per buyer per 24 hours.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          className={`shrink-0 relative inline-flex h-7 w-14 items-center rounded-full transition ${enabled ? "bg-[#F59E0B]" : "bg-[var(--line)]"}`}
          data-testid="dispo-autocounter-toggle"
          aria-pressed={enabled}
          aria-label="Toggle auto-counter"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enabled ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </div>
      {enabled && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <label className="text-xs text-[var(--ink-2)]">Counter bump ($ above current top):</label>
          <input
            type="number"
            min="0"
            step="100"
            value={bump}
            onChange={(e) => setBump(e.target.value)}
            onBlur={() => bump !== room.auto_counter_bump && save({ auto_counter_bump: Number(bump) })}
            className="input w-32 text-sm py-1.5"
            data-testid="dispo-autocounter-bump"
          />
          <span className="text-[10px] text-[var(--ink-3)]">Snapped to nearest $100 on send</span>
        </div>
      )}
    </div>
  );
}

